import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Paperclip, Type, Image as ImageIcon, Link as LinkIcon, Plus, Minus, MoreHorizontal, Trash2, Pencil } from 'lucide-react';
import StatusBadge from '../admin/StatusBadge';

const CHANGE_TYPE_META = {
  text: { label: 'Text', icon: Type, tone: 'neutral' },
  image: { label: 'Image', icon: ImageIcon, tone: 'accent' },
  link: { label: 'Link', icon: LinkIcon, tone: 'neutral' },
  new_section: { label: 'New section', icon: Plus, tone: 'success' },
  remove: { label: 'Remove', icon: Minus, tone: 'danger' },
  other: { label: 'Other', icon: MoreHorizontal, tone: 'muted' },
};

const STATUS_META = {
  open: { label: 'Open', tone: 'accent' },
  in_progress: { label: 'In progress', tone: 'warning' },
  resolved: { label: 'Resolved', tone: 'success' },
  closed: { label: 'Closed', tone: 'muted' },
};
const AWAITING_META = { label: 'Awaiting your reply', tone: 'waiting' };

// Same four levels and tones as tickets (TicketList.jsx).
const PRIORITY_META = {
  low: { label: 'Low', tone: 'success' },
  medium: { label: 'Medium', tone: 'warning' },
  high: { label: 'High', tone: 'warning' },
  critical: { label: 'Critical', tone: 'danger' },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function attachmentHref(key) {
  const segments = key.split('/').map(encodeURIComponent).join('/');
  return `/api/attachments/${segments}`;
}

// A request can be revised/withdrawn until staff resolve it.
const MUTABLE = new Set(['open', 'in_progress']);

export default function EditRequestList({ editRequests, loading, onDelete, onEdit, viewer }) {
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // Own submissions are always manageable; content-oversight holders (e.g. a
  // district director) can manage colleagues' too. Server re-enforces all of it.
  const canManage = (r) =>
    viewer && MUTABLE.has(r.status)
    && (r.submittedBy?.id === viewer.userId || viewer.contentAccess);

  const startEdit = (r) => {
    setEditingId(r.id);
    setDraft({
      title: r.title || '',
      description: r.description || '',
      priority: r.priority || 'medium',
      currentContent: r.currentContent || '',
      desiredContent: r.desiredContent || '',
      hasBeforeAfter: Boolean(r.currentContent || r.desiredContent),
    });
    setConfirmId(null);
  };

  const saveEdit = async (r) => {
    if (savingEdit) return;
    setSavingEdit(true);
    const fields = {
      title: draft.title,
      description: draft.description,
      priority: draft.priority,
    };
    if (draft.hasBeforeAfter) {
      fields.currentContent = draft.currentContent;
      fields.desiredContent = draft.desiredContent;
    }
    const ok = await onEdit?.(r.id, fields);
    setSavingEdit(false);
    if (ok !== false) setEditingId(null);
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 rounded-xl bg-white/[0.02] border border-white/[0.05] animate-pulse" />
        ))}
      </div>
    );
  }

  if (!editRequests || editRequests.length === 0) {
    return (
      <div className="p-8 rounded-2xl bg-white/[0.02] border border-white/[0.06] text-center">
        <div className="text-sm text-gray-400">No edit requests yet.</div>
        <div className="text-[12px] text-gray-600 mt-1">Submit your first request above.</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {editRequests.map((r) => {
        const isOpen = expandedId === r.id;
        const changeType = CHANGE_TYPE_META[r.changeType] || CHANGE_TYPE_META.other;
        // We asked them something: say so plainly, it is theirs to act on.
        const status = r.awaitingReply ? AWAITING_META : STATUS_META[r.status] || STATUS_META.open;
        const priority = PRIORITY_META[r.priority];
        const CIcon = changeType.icon;

        return (
          <div
            key={r.id}
            className="rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-hidden transition-all hover:border-white/[0.1]"
          >
            <button
              type="button"
              onClick={() => setExpandedId(isOpen ? null : r.id)}
              className="w-full flex items-center gap-3 px-4 md:px-5 py-3.5 text-left"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                  {priority && <StatusBadge tone={priority.tone}>{priority.label}</StatusBadge>}
                  <StatusBadge tone={changeType.tone} icon={CIcon}>
                    {changeType.label}
                  </StatusBadge>
                  {r.attachmentCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                      <Paperclip className="w-3 h-3" />
                      {r.attachmentCount}
                    </span>
                  )}
                </div>
                <div className="text-sm text-white truncate" title={r.title || r.description}>
                  <span className="text-gray-500 font-mono text-[12px] mr-2">{r.pagePath}</span>
                  <span>{r.title || previewDescription(r.description)}</span>
                </div>
              </div>
              <div className="text-[11px] text-gray-500 shrink-0 hidden sm:block">
                {formatDate(r.createdAt)}
              </div>
              <ChevronDown
                className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                  className="overflow-hidden border-t border-white/[0.04]"
                >
                  <div className="px-4 md:px-5 py-4 space-y-4">
                    <div>
                      <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">
                        Page
                      </div>
                      <div className="text-[13px] text-gray-200 font-mono">
                        {r.pageTitle ? <span>{r.pageTitle} · </span> : null}
                        {r.pagePath}
                      </div>
                    </div>

                    {editingId === r.id && (
                      <div className="space-y-3 p-3 rounded-xl bg-[#B0FC38]/[0.04] border border-[#B0FC38]/20">
                        <div>
                          <div className="text-[10px] font-medium text-gray-500 tracking-wider uppercase mb-1.5">Title</div>
                          <input
                            value={draft.title}
                            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                            className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-sm text-white focus:outline-none focus:border-[#B0FC38]/40"
                          />
                        </div>
                        <div>
                          <div className="text-[10px] font-medium text-gray-500 tracking-wider uppercase mb-1.5">Description</div>
                          <textarea
                            value={draft.description}
                            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                            rows={4}
                            className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[13px] text-white focus:outline-none focus:border-[#B0FC38]/40"
                          />
                        </div>
                        {draft.hasBeforeAfter && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <div className="text-[10px] font-medium text-gray-500 tracking-wider uppercase mb-1.5">Current</div>
                              <textarea
                                value={draft.currentContent}
                                onChange={(e) => setDraft((d) => ({ ...d, currentContent: e.target.value }))}
                                rows={3}
                                className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[12px] text-white focus:outline-none focus:border-[#B0FC38]/40"
                              />
                            </div>
                            <div>
                              <div className="text-[10px] font-medium text-gray-500 tracking-wider uppercase mb-1.5">Should become</div>
                              <textarea
                                value={draft.desiredContent}
                                onChange={(e) => setDraft((d) => ({ ...d, desiredContent: e.target.value }))}
                                rows={3}
                                className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[12px] text-white focus:outline-none focus:border-[#B0FC38]/40"
                              />
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <select
                            value={draft.priority}
                            onChange={(e) => setDraft((d) => ({ ...d, priority: e.target.value }))}
                            className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[12px] text-white focus:outline-none"
                          >
                            <option value="low">Low priority</option>
                            <option value="medium">Medium priority</option>
                            <option value="high">High priority</option>
                            <option value="critical">Critical priority</option>
                          </select>
                          <div className="flex-1" />
                          <button type="button" onClick={() => setEditingId(null)} disabled={savingEdit} className="px-3 py-2 rounded-lg border border-white/10 text-[12px] text-gray-400 hover:text-white transition-colors disabled:opacity-60">Cancel</button>
                          <button type="button" onClick={() => saveEdit(r)} disabled={savingEdit || !draft.description.trim()} className="px-4 py-2 rounded-lg bg-[#B0FC38] text-gray-900 text-[12px] font-semibold hover:bg-[#9BE22E] transition-colors disabled:opacity-60">
                            {savingEdit ? 'Saving…' : 'Save changes'}
                          </button>
                        </div>
                      </div>
                    )}

                    {editingId !== r.id && (
                    <div>
                      <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">
                        Description
                      </div>
                      <div className="text-[13px] text-gray-300 leading-relaxed whitespace-pre-wrap">
                        {r.description}
                      </div>
                    </div>
                    )}

                    {editingId !== r.id && (r.currentContent || r.desiredContent) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">Current</div>
                          <div className="text-[12px] text-gray-300 whitespace-pre-wrap px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.05] min-h-[2.5rem]">
                            {r.currentContent || '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] font-medium text-[#B0FC38] tracking-wider uppercase mb-1.5">Should become</div>
                          <div className="text-[12px] text-white whitespace-pre-wrap px-3 py-2 rounded-lg bg-[#B0FC38]/[0.06] border border-[#B0FC38]/20 min-h-[2.5rem]">
                            {r.desiredContent || '—'}
                          </div>
                        </div>
                      </div>
                    )}

                    {r.screenshotUrl && (
                      <div>
                        <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">
                          Screenshot
                        </div>
                        <a
                          href={`/api/edit-requests/${r.id}/screenshot`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block rounded-lg overflow-hidden border border-white/[0.08] hover:border-[#B0FC38]/40 transition-all max-w-lg"
                          title="Open full screenshot"
                        >
                          <img
                            src={`/api/edit-requests/${r.id}/screenshot`}
                            alt="Marked-up section of the page"
                            loading="lazy"
                            className="w-full block"
                          />
                        </a>
                      </div>
                    )}

                    {r.sectionSelector && (
                      <div>
                        <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">
                          Section selected
                        </div>
                        <div className="text-[11px] text-gray-400 font-mono break-all px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                          {r.sectionSelector}
                        </div>
                        {r.sectionPreview && (
                          <div className="text-[12px] text-gray-500 italic mt-1">"{r.sectionPreview}"</div>
                        )}
                      </div>
                    )}

                    {r.attachments && r.attachments.length > 0 && (
                      <div>
                        <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">
                          Attachments
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {r.attachments.map((a) => (
                            <a
                              key={a.key}
                              href={attachmentHref(a.key)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 hover:bg-white/[0.05] hover:border-white/[0.15] transition-all"
                            >
                              <Paperclip className="w-3 h-3 text-gray-500" />
                              <span className="truncate max-w-[200px]">{a.filename}</span>
                              <span className="text-gray-600">{formatBytes(a.size)}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="pt-2 border-t border-white/[0.04] flex items-center justify-between gap-4 text-[11px] text-gray-500">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span>Submitted {formatDate(r.createdAt)}</span>
                        {r.updatedAt !== r.createdAt && <span>· Updated {formatDate(r.updatedAt)}</span>}
                        {r.submittedBy?.name && <span>· by {r.submittedBy.name}</span>}
                      </div>
                      {/* Own submissions — or any, with content oversight — can be
                          edited/withdrawn until resolved. */}
                      {canManage(r) && editingId !== r.id && (
                        confirmId === r.id ? (
                          <span className="inline-flex items-center gap-2 shrink-0">
                            <span className="text-gray-400">Delete this request?</span>
                            <button type="button" onClick={() => { onDelete?.(r.id); setConfirmId(null); }} className="px-2 py-1 rounded-md bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25 transition-colors">Delete</button>
                            <button type="button" onClick={() => setConfirmId(null)} className="px-2 py-1 rounded-md border border-white/10 text-gray-400 hover:bg-white/[0.04] transition-colors">Cancel</button>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 shrink-0">
                            {onEdit && (
                              <button type="button" onClick={() => startEdit(r)} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-gray-500 hover:text-[#B0FC38] hover:bg-[#B0FC38]/10 transition-colors" title="Edit request">
                                <Pencil className="w-3.5 h-3.5" /> Edit
                              </button>
                            )}
                            {onDelete && (
                              <button type="button" onClick={() => setConfirmId(r.id)} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-gray-500 hover:text-red-300 hover:bg-red-500/10 transition-colors" title="Delete request">
                                <Trash2 className="w-3.5 h-3.5" /> Delete
                              </button>
                            )}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

function previewDescription(description) {
  if (!description) return '';
  const single = description.replace(/\s+/g, ' ').trim();
  return single.length > 80 ? `${single.slice(0, 78)}…` : single;
}
